import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconCopy, IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { joinPaths } from '~lib';
import { breakAll } from '~portal/components/common/common.css';
import { getAppEnv } from '~portal/env';
import { useAdminAccess } from '~portal/hooks';
import { logger } from '~portal/lib';

export const Route = createFileRoute('/_protected/admin/invite')({
  component: AdminInvitePage,
});

// -- Types for API responses --

type InviteResponse = {
  inviteId: string;
  token: string;
  orgId: string;
  expiresAt: string;
};

type ApiKey = {
  keyPrefix: string;
  orgId: string;
  label: string;
  createdAt: string;
  status: 'active' | 'revoked';
};

type KeysResponse = {
  keys: ApiKey[];
};

// -- Component --

function AdminInvitePage() {
  // Creating invites and editing keys are admin writes: an admin viewer (D21) lists keys only.
  const { write } = useAdminAccess();
  const { getAccessToken } = useAuth();
  const env = getAppEnv();
  const baseUrl = joinPaths(env.backendApiUrl, '/v1');

  // Form state
  const [orgId, setOrgId] = useState('');
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number>(7);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Created invite display
  const [createdInvite, setCreatedInvite] = useState<InviteResponse | null>(null);

  // Keys table state
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [keysLoaded, setKeysLoaded] = useState(false);

  // Revoke confirmation modal
  const [revokeModalOpened, { close: closeRevokeModal }] = useDisclosure(false);
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Label editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLabelValue, setEditLabelValue] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);

  // -- Fetch API keys --

  const fetchKeys = async () => {
    setKeysLoading(true);
    setKeysError(null);

    try {
      const token = await getAccessToken();

      const res = await fetch(joinPaths(baseUrl, '/admin/keys'), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to fetch keys: ${res.status} ${body}`);
      }

      const data: KeysResponse = await res.json();
      setKeys(data.keys);
      setKeysLoaded(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch API keys';
      setKeysError(message);
      logger.error('[AdminInvite] Failed to fetch keys', { error: message });
    } finally {
      setKeysLoading(false);
    }
  };

  // Fetch keys on first render
  if (!keysLoaded && !keysLoading) {
    fetchKeys();
  }

  // -- Create invite --

  const labelTooLong = label.length > 100;

  const handleCreateInvite = async () => {
    if (!orgId.trim()) {
      setFormError('Organisation ID is required');
      return;
    }

    if (!label.trim()) {
      setFormError('Label is required');
      return;
    }

    if (labelTooLong) {
      setFormError('Label must be 100 characters or fewer');
      return;
    }

    setCreating(true);
    setFormError(null);
    setCreatedInvite(null);

    try {
      const token = await getAccessToken();

      const res = await fetch(joinPaths(baseUrl, '/admin/invites'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orgId: orgId.trim(),
          label: label.trim(),
          expiresInDays,
        }),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to create invite: ${res.status} ${body}`);
      }

      const data: InviteResponse = await res.json();
      setCreatedInvite(data);

      // Reset form
      setOrgId('');
      setLabel('');
      setExpiresInDays(7);

      // Refresh keys table
      fetchKeys();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create invite';
      setFormError(message);
      logger.error('[AdminInvite] Failed to create invite', { error: message });
    } finally {
      setCreating(false);
    }
  };

  // -- Revoke key --

  const handleRevokeKey = async () => {
    if (!pendingRevoke) return;

    setRevoking(true);

    try {
      const token = await getAccessToken();

      const res = await fetch(joinPaths(baseUrl, `/admin/keys/${pendingRevoke}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to revoke key: ${res.status} ${body}`);
      }

      closeRevokeModal();
      setPendingRevoke(null);

      // Refresh keys table
      fetchKeys();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke key';
      setKeysError(message);
      logger.error('[AdminInvite] Failed to revoke key', { error: message });
      closeRevokeModal();
      setPendingRevoke(null);
    } finally {
      setRevoking(false);
    }
  };

  // -- Update key label --

  const startEditingLabel = (key: ApiKey) => {
    setEditingKey(key.keyPrefix);
    setEditLabelValue(key.label);
  };

  const cancelEditingLabel = () => {
    setEditingKey(null);
    setEditLabelValue('');
  };

  const handleUpdateLabel = async () => {
    if (!editingKey) return;

    const trimmed = editLabelValue.trim();
    if (!trimmed) {
      setKeysError('Label is required');
      return;
    }
    if (trimmed.length > 100) {
      setKeysError('Label must be 100 characters or fewer');
      return;
    }

    setSavingLabel(true);

    try {
      const token = await getAccessToken();

      const res = await fetch(joinPaths(baseUrl, `/admin/keys/${editingKey}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label: trimmed }),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to update label: ${res.status} ${body}`);
      }

      cancelEditingLabel();
      fetchKeys();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update label';
      setKeysError(message);
      logger.error('[AdminInvite] Failed to update label', { error: message });
    } finally {
      setSavingLabel(false);
    }
  };

  const claimLink = createdInvite ? `${window.location.origin}/claim/${createdInvite.token}` : null;

  return (
    <Stack gap="lg" p="lg">
      {/* Revoke Confirmation Modal */}
      <Modal
        opened={revokeModalOpened}
        onClose={() => {
          closeRevokeModal();
          setPendingRevoke(null);
        }}
        title="Confirm Revocation"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to revoke the API key with prefix{' '}
            <Text span fw={600}>
              {pendingRevoke}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                closeRevokeModal();
                setPendingRevoke(null);
              }}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleRevokeKey} loading={revoking}>
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Title order={1}>Subscriber API Keys</Title>

      {/* Create Invite Form */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Title order={3}>Create Invite</Title>

          <TextInput
            label="Organisation ID"
            placeholder="e.g. org_abc123"
            required
            value={orgId}
            onChange={(e) => setOrgId(e.currentTarget.value)}
          />

          <TextInput
            label="Label"
            description="Human-readable label to identify this token in logs and traces"
            placeholder="e.g. Acme Corp Production"
            required
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            error={labelTooLong ? `${label.length}/100 characters` : undefined}
          />

          <NumberInput
            label="Expiry (days)"
            description="Number of days until the invite expires"
            value={expiresInDays}
            onChange={(val) => setExpiresInDays(typeof val === 'number' ? val : 7)}
            min={1}
            max={30}
            w={200}
          />

          {formError && (
            <Alert color="red" title="Error" withCloseButton onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <Group>
            <Button onClick={handleCreateInvite} loading={creating} disabled={!write}>
              Create Invite
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Generated Invite Display */}
      {createdInvite && claimLink && (
        <Alert color="green" title="Invite Created Successfully" withCloseButton={false}>
          <Stack gap="sm">
            <Text size="sm" fw={600} c="red">
              This token is shown only once. Copy it now.
            </Text>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Raw Token
              </Text>
              <Group gap="xs">
                <Code block flex={1} className={breakAll}>
                  {createdInvite.token}
                </Code>
                <CopyButton value={createdInvite.token}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy token'}>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={copy}
                        color={copied ? 'teal' : 'gray'}
                      >
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </Button>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </div>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Claim Link
              </Text>
              <Group gap="xs">
                <Code block flex={1} className={breakAll}>
                  {claimLink}
                </Code>
                <CopyButton value={claimLink}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy link'}>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={copy}
                        color={copied ? 'teal' : 'gray'}
                      >
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </Button>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </div>

            <Group gap="lg">
              <Text size="xs" c="dimmed">
                Organisation: {createdInvite.orgId}
              </Text>
              <Text size="xs" c="dimmed">
                Expires: {new Date(createdInvite.expiresAt).toLocaleDateString()}
              </Text>
            </Group>
          </Stack>
        </Alert>
      )}

      {/* API Keys Table */}
      <Paper withBorder p="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>API Keys</Title>
          <Button onClick={fetchKeys} loading={keysLoading} size="xs" variant="light">
            Refresh
          </Button>
        </Group>

        {keysError && (
          <Alert
            color="red"
            title="Error"
            mb="md"
            withCloseButton
            onClose={() => setKeysError(null)}
          >
            {keysError}
          </Alert>
        )}

        {keys.length === 0 && !keysLoading ? (
          <Text c="dimmed" size="sm">
            No API keys found
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Key Prefix</Table.Th>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Org ID</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {keys.map((key) => (
                  <Table.Tr key={key.keyPrefix}>
                    <Table.Td>
                      <Code>{key.keyPrefix}</Code>
                    </Table.Td>
                    <Table.Td>
                      {editingKey === key.keyPrefix ? (
                        <Group gap="xs" wrap="nowrap">
                          <TextInput
                            size="xs"
                            value={editLabelValue}
                            onChange={(e) => setEditLabelValue(e.currentTarget.value)}
                            placeholder="Label"
                            error={editLabelValue.length > 100}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateLabel();
                              if (e.key === 'Escape') cancelEditingLabel();
                            }}
                            // eslint-disable-next-line jsx-a11y/no-autofocus -- the field appears in place of the label the admin just clicked to edit, so focus follows their action
                            autoFocus
                            flex={1}
                          />
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="teal"
                            onClick={handleUpdateLabel}
                            loading={savingLabel}
                          >
                            <IconCheck size={14} />
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="gray"
                            onClick={cancelEditingLabel}
                          >
                            <IconX size={14} />
                          </Button>
                        </Group>
                      ) : (
                        <Group gap={4} wrap="nowrap">
                          <Text size="sm">{key.label}</Text>
                          <Tooltip label="Edit label">
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="gray"
                              onClick={() => startEditingLabel(key)}
                              disabled={!write}
                            >
                              <IconPencil size={14} />
                            </Button>
                          </Tooltip>
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{key.orgId}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{new Date(key.createdAt).toLocaleDateString()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={key.status === 'active' ? 'green' : 'red'}
                        variant="filled"
                        size="sm"
                      >
                        {key.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {key.status === 'active' && (
                        // data-disabled, not disabled: a disabled button swallows the pointer
                        // events the WIP tooltip needs.
                        <Tooltip label="WIP">
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            data-disabled
                            onClick={(event) => event.preventDefault()}
                          >
                            Revoke
                          </Button>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}
